import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Locale } from './locales';
import { SUPPORTED_LOCALES } from './locales';
import { initialLocale, withoutLocaleParams } from './detect';
import { getTranslation } from './translations';
import type { TranslationKeys } from './translations/en';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationKeys;
}

const I18nContext = createContext<I18nContextType | null>(null);

const LOCALE_STORAGE_KEY = 'portal-k8s-locale';

// Decided once, when the app loads: before the router reads the address, so
// dropping the ?lang= parameter from it is invisible to the routes.
function resolveStartLocale(): Locale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to read locale from localStorage:', e);
  }
  if (typeof window === 'undefined') return initialLocale({ search: '', stored, languages: [] }).locale;
  const { locale, fromLink } = initialLocale({ search: window.location.search, stored, languages: navigator.languages ?? [navigator.language] });
  if (fromLink) {
    setStoredLocale(locale);
    window.history.replaceState(window.history.state, '', withoutLocaleParams(window.location.href));
  }
  return locale;
}

function setStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (e) {
    console.warn('Failed to save locale to localStorage:', e);
  }
}

const START_LOCALE = resolveStartLocale();

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(START_LOCALE);
  const [t, setT] = useState<TranslationKeys>(() => getTranslation(locale));

  const setLocale = (newLocale: Locale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      setStoredLocale(newLocale);
      setT(getTranslation(newLocale));
    }
  };

  useEffect(() => {
    setT(getTranslation(locale));
    document.documentElement.lang = locale;
  }, [locale]);

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}

export function useTranslation() {
  return useI18n().t;
}

export function format(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}
